import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    Tabs,
    Tab,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    CardActions,
    List,
    ListItem,
    ListItemText,
    InputAdornment,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    CalendarMonth as CalendarIcon,
    Group as GroupIcon,
    Settings as SettingsIcon,
    AdminPanelSettings as AdminIcon,
    Logout as LogoutIcon,
    CorporateFare,
    CloudUpload as CloudUploadIcon,
    Landscape,
    ElectricBolt,
    Construction,
    HomeRepairService,
    Business as BusinessIcon,
    WaterDrop,
    FormatPaint,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Dashboard as DashboardIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Visibility as VisibilityIcon,
    Star as StarIcon,
    RequestQuote as QuoteIcon,
    Category as CategoryIcon,
    AttachMoney as MoneyIcon,
    SaveAlt as SaveIcon,
    Print as PrintIcon,
    LocalShipping as LocalShippingIcon,
    MoreHoriz as MoreHorizIcon,
    TableView as TableViewIcon,
    Send as SendIcon,
    Search as SearchIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    ViewHeadline as ViewHeadlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectService, userService, quoteService } from '../services/api';
import { API_URL } from '../services/api';
import '../styles/Admin.scss';
import UnderDevelopment from '../components/UnderDevelopment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const Admin = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Projektek kezelése
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectDialog, setProjectDialog] = useState(false);
    const [projectForm, setProjectForm] = useState({
        title: '',
        location: '',
        description: '',
        type: 'commercial',
        startDate: '',
        endDate: '',
        status: 'pending',
        featured: false,
        images: [],
        newImages: [],
    });

    // Felhasználók kezelése
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDialog, setUserDialog] = useState(false);
    const [userForm, setUserForm] = useState({
        username: '',
        password: '',
        name: '',
        email: '',
        phone: '',
        role: 'owner'
    });

    // Kép kezelés
    const [selectedImage, setSelectedImage] = useState(null);

    // Új state-ek a dashboard adatokhoz
    const [dashboardStats, setDashboardStats] = useState({
        totalProjects: 0,
        activeProjects: 0,
        totalUsers: 0,
        totalViews: 0,
        recentViews: [],
        pageViews: [],
        projectStats: {
            completed: 0,
            inProgress: 0,
            pending: 0
        }
    });

    // Árajánlat kezelés
    const [quoteDialog, setQuoteDialog] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [quoteForm, setQuoteForm] = useState({
        projectName: '',
        projectType: '',
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        description: '',
        subtotal: 0,
        tax: 27,
        total: 0,
        validUntil: '',
        terms: '',
        notes: ''
    });

    const [quoteItems, setQuoteItems] = useState([]);

    // Új state-ek az árajánlat kezeléshez
    const [quoteView, setQuoteView] = useState('grid'); // 'grid', 'list', 'details'
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredQuotes, setFilteredQuotes] = useState([]);

    // Költségtételek kategóriái
    const itemCategories = [
        { id: 'material', label: 'Anyagköltség', icon: <CategoryIcon /> },
        { id: 'labor', label: 'Munkadíj', icon: <MoneyIcon /> },
        { id: 'equipment', label: 'Eszközök', icon: <Construction /> },
        { id: 'transport', label: 'Szállítás', icon: <LocalShippingIcon /> },
        { id: 'other', label: 'Egyéb', icon: <MoreHorizIcon /> }
    ];

    const projectTypes = [
        { value: 'commercial', label: 'Kereskedelmi ingatlan', icon: <CorporateFare /> },
        { value: 'alpine', label: 'Alpintechnika', icon: <Landscape /> },
        { value: 'electrical', label: 'Épületvillamosság', icon: <ElectricBolt /> },
        { value: 'insulation', label: 'Szigeteléstechnika', icon: <Construction /> },
        { value: 'renovation', label: 'Felújítás', icon: <HomeRepairService /> },
        { value: 'facade', label: 'Homlokzati munkák', icon: <BusinessIcon /> },
        { value: 'waterproofing', label: 'Vízszigetelés', icon: <WaterDrop /> },
        { value: 'painting', label: 'Festés-mázolás', icon: <FormatPaint /> }
    ];

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            if (activeTab === 0) { // Projektek
                const response = await projectService.getAllProjects();
                setProjects(response.projects);
            } else if (activeTab === 1) { // Felhasználók
                const response = await userService.getAllUsers();
                setUsers(response.users);
            }
        } catch (err) {
            console.error('Hiba az adatok betöltése során:', err);
            setError(err.message || 'Hiba történt az adatok betöltése során.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 0) { // Dashboard
            fetchDashboardData();
        }
    }, [activeTab]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            // Itt majd az API hívások jönnek a dashboard adatok lekéréséhez
            // Példa adatok demonstrációhoz
            setDashboardStats({
                totalProjects: projects.length,
                activeProjects: projects.filter(p => p.status === 'in_progress').length,
                totalUsers: users.length,
                totalViews: 15420,
                recentViews: [
                    { page: 'Főoldal', views: 523, trend: '+12%' },
                    { page: 'Projektek', views: 342, trend: '+8%' },
                    { page: 'Kapcsolat', views: 234, trend: '+5%' }
                ],
                pageViews: [
                    { date: '2024-03-01', views: 120 },
                    { date: '2024-03-02', views: 145 },
                    { date: '2024-03-03', views: 165 },
                    { date: '2024-03-04', views: 140 },
                    { date: '2024-03-05', views: 180 }
                ],
                projectStats: {
                    completed: projects.filter(p => p.status === 'completed').length,
                    inProgress: projects.filter(p => p.status === 'in_progress').length,
                    pending: projects.filter(p => p.status === 'pending').length
                }
            });
        } catch (error) {
            console.error('Hiba a dashboard adatok betöltése során:', error);
        } finally {
            setLoading(false);
        }
    };

    // UI kezelés
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const handleProjectDialog = (project = null) => {
        if (project) {
            setSelectedProject(project);
            setProjectForm({
                title: project.title,
                location: project.location || '',
                description: project.description || '',
                type: project.type || '',
                startDate: project.start_date ? project.start_date.split('T')[0] : '',
                endDate: project.end_date ? project.end_date.split('T')[0] : '',
                status: project.status || 'pending',
                featured: project.featured || false,
                images: project.images || [],
                newImages: [],
            });
        } else {
            setSelectedProject(null);
            resetForms();
        }
        setProjectDialog(true);
    };

    const handleUserDialog = (user = null) => {
        if (user) {
            setSelectedUser(user);
            setUserForm({
                username: user.username,
                name: user.name,
                email: user.email,
                phone: user.phone,
                role: user.role,
            });
        } else {
            setSelectedUser(null);
            resetForms();
        }
        setUserDialog(true);
    };

    const resetForms = () => {
        setProjectForm({
            title: '',
            location: '',
            description: '',
            type: '',
            startDate: '',
            endDate: '',
            status: 'pending',
            featured: false,
            images: [],
            newImages: [],
        });
        setUserForm({
            username: '',
            password: '',
            name: '',
            email: '',
            phone: '',
            role: 'owner'
        });
    };

    const handleProjectSubmit = async (e) => {
        e.preventDefault();

        // Validáció
        if (!projectForm.title?.trim()) {
            setError('A projekt címe kötelező!');
            return;
        }
        if (!projectForm.type) {
            setError('A projekt típusa kötelező!');
            return;
        }

        // Kiemelt projekt validáció
        if (projectForm.featured && projectForm.status !== 'completed') {
            setError('Csak befejezett projektet lehet kiemelt referenciaként megjelölni!');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();

            // Alap adatok hozzáadása
            formData.append('title', projectForm.title.trim());
            formData.append('location', projectForm.location?.trim() || '');
            formData.append('description', projectForm.description?.trim() || '');
            formData.append('type', projectForm.type);
            formData.append('start_date', projectForm.startDate || '');
            formData.append('end_date', projectForm.endDate || '');
            formData.append('status', projectForm.status || 'pending');
            formData.append('featured', projectForm.featured || false);

            // Új képek hozzáadása
            if (projectForm.newImages && projectForm.newImages.length > 0) {
                projectForm.newImages.forEach(file => {
                    formData.append('images', file);
                });
            }

            if (selectedProject) {
                // Meglévő képek hozzáadása csak szerkesztésnél
                if (projectForm.images && projectForm.images.length > 0) {
                    formData.append('existing_images', JSON.stringify(projectForm.images));
                }
                await projectService.updateProject(selectedProject.id, formData);
                setSuccess('Projekt sikeresen frissítve!');
            } else {
                await projectService.createProject(formData);
                setSuccess('Projekt sikeresen létrehozva!');
            }
            setProjectDialog(false);
            fetchData();
        } catch (err) {
            console.error('Hiba történt:', err);
            setError(err.response?.data?.message || err.message || 'Hiba történt a projekt mentése során.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProject = async (id) => {
        if (window.confirm('Biztosan törölni szeretné ezt a projektet?')) {
            try {
                setLoading(true);
                await projectService.deleteProject(id);
                setSuccess('Projekt sikeresen törölve!');
                fetchData();
            } catch (err) {
                setError(err.message || 'Hiba történt a projekt törlése során.');
            } finally {
                setLoading(false);
            }
        }
    };

    // Felhasználó kezelés
    const handleUserSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            if (selectedUser) {
                await userService.updateUser(selectedUser.id, userForm);
                setSuccess('Felhasználó sikeresen frissítve!');
            } else {
                await userService.createUser(userForm);
                setSuccess('Felhasználó sikeresen létrehozva!');
            }
            setUserDialog(false);
            fetchData();
        } catch (err) {
            setError(err.message || 'Hiba történt a felhasználó mentése során.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (id) => {
        if (window.confirm('Biztosan törölni szeretné ezt a felhasználót?')) {
            try {
                setLoading(true);
                await userService.deleteUser(id);
                setSuccess('Felhasználó sikeresen törölve!');
                fetchData();
            } catch (err) {
                setError(err.message || 'Hiba történt a felhasználó törlése során.');
            } finally {
                setLoading(false);
            }
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'new':
                return 'Új';
            case 'sent':
                return 'Elküldve';
            case 'accepted':
                return 'Elfogadva';
            case 'rejected':
                return 'Elutasítva';
            default:
                return 'Új';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'new':
                return 'info';
            case 'sent':
                return 'primary';
            case 'accepted':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'info';
        }
    };

    // Kép kezelés
    const handleImageClick = (image) => {
        setSelectedImage(image);
    };

    const handleCloseImageViewer = () => {
        setSelectedImage(null);
    };

    const handleDeleteImage = async (projectId, imagePath) => {
        if (window.confirm('Biztosan törölni szeretné ezt a képet?')) {
            try {
                setLoading(true);
                await projectService.deleteProjectImage(projectId, imagePath);
                setSuccess('Kép sikeresen törölve!');
                fetchData();
            } catch (err) {
                setError(err.message || 'Hiba történt a kép törlése során.');
            } finally {
                setLoading(false);
            }
        }
    };

    // Árajánlat kezelő függvények
    const handleAddQuoteItem = () => {
        setQuoteItems([
            ...quoteItems,
            {
                id: Date.now(),
                category: '',
                description: '',
                quantity: '',
                unit: 'db',
                unitPrice: '',
                total: 0
            }
        ]);
    };

    const handleQuoteItemChange = (id, field, value) => {
        const updatedItems = quoteItems.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'quantity' || field === 'unitPrice') {
                    updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
                }
                return updatedItem;
            }
            return item;
        });
        setQuoteItems(updatedItems);
        calculateTotals(updatedItems);
    };

    const calculateTotals = (items) => {
        const subtotal = items.reduce((sum, item) => sum + item.total, 0);
        const taxAmount = subtotal * (quoteForm.tax / 100);
        const total = subtotal + taxAmount;

        setQuoteForm(prev => ({
            ...prev,
            subtotal,
            total
        }));
    };

    const handleQuoteSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);

            // Validate required fields
            if (!quoteForm.projectName || !quoteForm.clientName || !quoteItems.length) {
                setError('Kérjük töltse ki a kötelező mezőket (projekt neve, ügyfél neve, és legalább egy tétel)!');
                setLoading(false);
                return;
            }

            const quoteData = {
                project_name: quoteForm.projectName,
                project_type: quoteForm.projectType,
                client_name: quoteForm.clientName,
                client_email: quoteForm.clientEmail,
                client_phone: quoteForm.clientPhone,
                description: quoteForm.description,
                subtotal: quoteForm.subtotal,
                tax_rate: quoteForm.tax,
                tax_amount: quoteForm.subtotal * (quoteForm.tax / 100),
                total: quoteForm.total,
                valid_until: quoteForm.validUntil,
                notes: quoteForm.notes,
                items: quoteItems.map(item => ({
                    category: item.category,
                    description: item.description,
                    quantity: parseFloat(item.quantity),
                    unit: item.unit,
                    unit_price: parseFloat(item.unitPrice),
                    total: parseFloat(item.total)
                }))
            };

            if (selectedQuote) {
                await quoteService.updateQuote(selectedQuote.id, quoteData);
                setSuccess('Árajánlat sikeresen frissítve!');
            } else {
                await quoteService.createQuote(quoteData);
                setSuccess('Árajánlat sikeresen létrehozva!');
            }

            setQuoteDialog(false);
            fetchQuotes();
        } catch (err) {
            console.error('Quote error:', err);
            setError(err.message || 'Hiba történt az árajánlat mentése során.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuote = async (id) => {
        if (window.confirm('Biztosan törli ezt az árajánlatot?')) {
            try {
                setLoading(true);
                await quoteService.deleteQuote(id);
                setSuccess('Árajánlat sikeresen törölve!');
                fetchQuotes();
            } catch (err) {
                setError(err.message || 'Hiba történt az árajánlat törlése során.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEditQuote = (quote) => {
        setSelectedQuote(quote);
        setQuoteForm({
            projectName: quote.project_name,
            clientName: quote.client_name,
            clientEmail: quote.client_email,
            clientPhone: quote.client_phone,
            projectType: quote.project_type,
            description: quote.description,
            subtotal: quote.subtotal,
            tax: quote.tax_rate,
            total: quote.total,
            validUntil: quote.valid_until,
            terms: quote.terms,
            notes: quote.notes
        });
        setQuoteItems(quote.items || []);
        setQuoteDialog(true);
    };

    const handleExportPDF = (quote) => {
        try {
            // A4 méret, álló formátum
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const margin = 20;

            // Betűtípus beállítása
            doc.setFont('helvetica');
            
            // Fejléc háttér - elegáns színátmenet hatás
            const headerHeight = 40;
            
            // Színátmenet szimulálása több téglalappal
            const segments = 20;  // Szegmensek száma
            const segmentWidth = pageWidth / segments;
            
            for (let i = 0; i < segments; i++) {
                const ratio = i / segments;
                // Színek lineáris interpolációja
                const r = Math.round(255 * (1 - ratio));
                const g = Math.round((255 * (1 - ratio) + 150 * ratio));
                const b = Math.round((255 * (1 - ratio) + 136 * ratio));
                
                doc.setFillColor(r, g, b);
                doc.rect(i * segmentWidth, 0, segmentWidth + 0.5, headerHeight, 'F');
            }

            // Logo beillesztése
            try {
                doc.addImage(`${API_URL}/assets/logo.png`, 'PNG', margin, 5, 30, 30);
            } catch (error) {
                console.warn('Logo betöltése sikertelen:', error);
            doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255);
                doc.text('TORONYSZERVER', margin, 25);
            }

            // Árajánlat felirat
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.text('ÁRAJÁNLAT', pageWidth - margin, 25, { align: 'right' });

            // Egyedi azonosító és dátum
            const quoteNumber = `AJ-${new Date().getFullYear()}${String(quote.id).padStart(4, '0')}`;
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            doc.text(`Árajánlat száma: ${quoteNumber}`, margin, 50);
            doc.text(`Kelt: ${new Date().toLocaleDateString('hu-HU')}`, margin, 57);
            doc.text(`Érvényes: ${new Date(quote.valid_until).toLocaleDateString('hu-HU')}`, pageWidth - margin, 50, { align: 'right' });

            // Kiállító és megrendelő adatok
            doc.setFillColor(250, 250, 250);
            doc.rect(margin, 65, (pageWidth - 2 * margin) / 2 - 5, 35, 'F');
            doc.rect(pageWidth/2 + 5, 65, (pageWidth - 2 * margin) / 2 - 5, 35, 'F');

            // Kiállító
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Kiállító adatok:', margin + 5, 73);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text('Toronyszerver Kft.', margin + 5, 81);
            doc.text('2471 Baracska Akácos utca 2430', margin + 5, 88);
            doc.text('32481392-1-07', margin + 5, 95);

            // Megrendelő - UTF-8 kódolással
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            const megrendeloText = 'Ügyfél adatok:';
            doc.text(megrendeloText, pageWidth/2 + 10, 73);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(quote.client_name, pageWidth/2 + 10, 81);
            doc.text(quote.client_email || '', pageWidth/2 + 10, 88);
            doc.text(quote.client_phone || '', pageWidth/2 + 10, 95);

            // Tételek táblázat
            const tableData = quote.items.map(item => [
                item.description,
                item.quantity + ' ' + item.unit,
                Number(item.unit_price).toLocaleString('hu-HU') + ' HUF',
                Number(item.total).toLocaleString('hu-HU') + ' HUF'
            ]);

            doc.autoTable({
                startY: 110,
                head: [['Tétel', 'Mennyiség', 'Egységár', 'Összesen']],
                body: tableData,
                headStyles: {
                    fillColor: [245, 245, 245],
                    textColor: [80, 80, 80],
                    fontStyle: 'bold',
                    halign: 'left'
                },
                bodyStyles: {
                    fontSize: 10,
                    lineColor: [220, 220, 220],
                    lineWidth: 0.1,
                    textColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 80 },
                    1: { cellWidth: 30, halign: 'center' },
                    2: { cellWidth: 30, halign: 'right' },
                    3: { cellWidth: 30, halign: 'right' }
                },
                margin: { left: margin, right: margin },
                alternateRowStyles: {
                    fillColor: [250, 250, 250]
                }
            });

            // Összesítés
            const finalY = doc.lastAutoTable.finalY + 10;
            doc.setFillColor(245, 245, 245);
            doc.rect(pageWidth - margin - 80, finalY, 80, 30, 'F');
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            doc.text(`Nettó összesen: ${Number(quote.subtotal).toLocaleString('hu-HU')} HUF`, pageWidth - margin - 5, finalY + 8, { align: 'right' });
            doc.text(`ÁFA (${quote.tax_rate}%): ${Number(quote.tax_amount).toLocaleString('hu-HU')} HUF`, pageWidth - margin - 5, finalY + 16, { align: 'right' });
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(`Bruttó összesen: ${Number(quote.total).toLocaleString('hu-HU')} HUF`, pageWidth - margin - 5, finalY + 24, { align: 'right' });

            // Megjegyzések
            if (quote.notes) {
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('Megjegyzés:', margin, finalY + 45);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const splitNotes = doc.splitTextToSize(quote.notes, pageWidth - 2 * margin);
                doc.text(splitNotes, margin, finalY + 52);
            }

            // Lábléc
            doc.setFillColor(245, 245, 245);
            doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');
            doc.setTextColor(80, 80, 80);
            doc.setFontSize(8);
            doc.text('Toronyszerver Kft.', margin, pageHeight - 15);
            doc.text('Tel: +36 1 234 5678', pageWidth/2, pageHeight - 15, { align: 'center' });
            doc.text('Email: info@toronyszerver.hu', pageWidth/2, pageHeight - 10, { align: 'center' });
            doc.text('Adószám: 12345678-2-42', pageWidth - margin, pageHeight - 15, { align: 'right' });
            doc.text('Cégjegyzékszám: 01-09-123456', pageWidth - margin, pageHeight - 10, { align: 'right' });

            // PDF mentése
            doc.save(`arajanlat_${quoteNumber}_${quote.project_name.replace(/\s+/g, '_')}.pdf`);
            setSuccess('PDF sikeresen létrehozva!');
        } catch (error) {
            console.error('PDF generálási hiba:', error);
            setError('Hiba történt a PDF generálása során.');
        }
    };

    const handleExportExcel = (quote) => {
        try {
            const workbook = XLSX.utils.book_new();
            
            // Stílusok definiálása
            const headerStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "2C5049" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                }
            };
            
            const normalStyle = {
                alignment: { vertical: "center" },
                border: {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                }
            };
            
            const totalStyle = {
                font: { bold: true },
                fill: { fgColor: { rgb: "F4F4F4" } },
                alignment: { horizontal: "right", vertical: "center" },
                border: {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                }
            };

            // Fejléc munkalap
            const headerData = [
                ['ÁRAJÁNLAT'],
                [''],
                ['Árajánlat száma:', quote.id],
                ['Dátum:', new Date().toLocaleDateString('hu-HU')],
                [''],
                ['PROJEKT ADATOK'],
                ['Projekt neve:', quote.project_name],
                ['Projekt típusa:', projectTypes.find(t => t.value === quote.project_type)?.label || ''],
                [''],
                ['ÜGYFÉL ADATOK'],
                ['Név:', quote.client_name],
                ['Email:', quote.client_email || '-'],
                ['Telefon:', quote.client_phone || '-'],
                ['Érvényesség:', new Date(quote.valid_until).toLocaleDateString('hu-HU')]
            ];
            
            const headerSheet = XLSX.utils.aoa_to_sheet(headerData);
            XLSX.utils.book_append_sheet(workbook, headerSheet, 'Árajánlat adatok');

            // Tételek munkalap
            const itemsHeader = ['Kategória', 'Megnevezés', 'Mennyiség', 'Egység', 'Egységár (Ft)', 'Összesen (Ft)'];
            const itemsData = quote.items.map(item => [
                itemCategories.find(cat => cat.id === item.category)?.label || '',
                item.description,
                Number(item.quantity),
                item.unit,
                Number(item.unit_price),
                Number(item.total)
            ]);
            
            // Összesítő sorok
            const summaryRows = [
                ['', '', '', '', 'Nettó összesen:', Number(quote.subtotal)],
                ['', '', '', '', `ÁFA (${quote.tax_rate}%):`, Number(quote.tax_amount)],
                ['', '', '', '', 'Bruttó összesen:', Number(quote.total)]
            ];
            
            const itemsSheet = XLSX.utils.aoa_to_sheet([
                itemsHeader,
                ...itemsData,
                [''],
                ...summaryRows
            ]);

            // Oszlopszélességek beállítása
            const colWidths = [20, 40, 10, 10, 15, 15];
            itemsSheet['!cols'] = colWidths.map(w => ({ wch: w }));

            XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Tételek');

            // Megjegyzések munkalap
            if (quote.notes) {
                const notesData = [
                    ['MEGJEGYZÉSEK'],
                    [''],
                    [quote.notes]
                ];
                const notesSheet = XLSX.utils.aoa_to_sheet(notesData);
                XLSX.utils.book_append_sheet(workbook, notesSheet, 'Megjegyzések');
            }

            // Excel fájl mentése
            XLSX.writeFile(workbook, `arajanlat_${quote.project_name.replace(/\s+/g, '_')}.xlsx`);
            setSuccess('Excel fájl sikeresen létrehozva!');
        } catch (error) {
            console.error('Excel generálási hiba:', error);
            setError('Hiba történt az Excel fájl generálása során.');
        }
    };

    const handleSendEmail = async (quote) => {
        try {
            setLoading(true);
            
            // HTML email sablon létrehozása
            const emailTemplate = {
                to: quote.client_email,
                subject: `Árajánlat - ${quote.project_name}`,
                html: `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="utf-8">
                        <style>
                            body {
                                font-family: Arial, sans-serif;
                                line-height: 1.6;
                                color: #333;
                                max-width: 800px;
                                margin: 0 auto;
                            }
                            .header {
                                background: linear-gradient(to right, #ffffff, #009688);
                                padding: 20px;
                                text-align: center;
                                border-radius: 5px 5px 0 0;
                            }
                            .content {
                                padding: 30px;
                                background: #fff;
                                border: 1px solid #eee;
                            }
                            .quote-details {
                                background: #f9f9f9;
                                padding: 20px;
                                border-radius: 5px;
                                margin: 20px 0;
                            }
                            .quote-number {
                                color: #009688;
                                font-size: 18px;
                                font-weight: bold;
                            }
                            .total {
                                font-size: 20px;
                                color: #009688;
                                text-align: right;
                                margin-top: 20px;
                                padding-top: 20px;
                                border-top: 2px solid #eee;
                            }
                            .footer {
                                background: #f5f5f5;
                                padding: 20px;
                                text-align: center;
                                font-size: 12px;
                                color: #666;
                                border-radius: 0 0 5px 5px;
                            }
                            table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 20px 0;
                            }
                            th, td {
                                padding: 12px;
                                text-align: left;
                                border-bottom: 1px solid #eee;
                            }
                            th {
                                background: #f5f5f5;
                            }
                            .button {
                                display: inline-block;
                                padding: 12px 24px;
                                background: #009688;
                                color: white;
                                text-decoration: none;
                                border-radius: 5px;
                                margin-top: 20px;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <img src="${API_URL}/assets/logo.png" alt="Toronyszerver Logo" style="max-height: 60px;">
                        </div>
                        <div class="content">
                            <h2>Tisztelt ${quote.client_name}!</h2>
                            
                            <p>Köszönjük megtisztelő érdeklődését szolgáltatásaink iránt. Mellékelten küldjük a kért árajánlatunkat a következő projektre:</p>
                            
                            <div class="quote-details">
                                <p class="quote-number">Árajánlat száma: AJ-${new Date().getFullYear()}${String(quote.id).padStart(4, '0')}</p>
                                <p><strong>Projekt:</strong> ${quote.project_name}</p>
                                <p><strong>Érvényesség:</strong> ${new Date(quote.valid_until).toLocaleDateString('hu-HU')}</p>
                            </div>

                            <table>
                                <thead>
                                    <tr>
                                        <th>Megnevezés</th>
                                        <th>Mennyiség</th>
                                        <th>Egységár</th>
                                        <th>Összesen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${quote.items.map(item => `
                                        <tr>
                                            <td>${item.description}</td>
                                            <td>${item.quantity} ${item.unit}</td>
                                            <td>${Number(item.unit_price).toLocaleString('hu-HU')} Ft</td>
                                            <td>${Number(item.total).toLocaleString('hu-HU')} Ft</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>

                            <div class="total">
                                <p><strong>Nettó összesen:</strong> ${Number(quote.subtotal).toLocaleString('hu-HU')} Ft</p>
                                <p><strong>ÁFA (${quote.tax_rate}%):</strong> ${Number(quote.tax_amount).toLocaleString('hu-HU')} Ft</p>
                                <p><strong>Bruttó összesen:</strong> ${Number(quote.total).toLocaleString('hu-HU')} Ft</p>
                            </div>

                            ${quote.notes ? `
                                <div style="margin-top: 20px;">
                                    <h3>Megjegyzések</h3>
                                    <p>${quote.notes}</p>
                                </div>
                            ` : ''}

                            <p style="margin-top: 30px;">
                                Az árajánlattal kapcsolatos esetleges kérdéseivel forduljon hozzánk bizalommal az alábbi elérhetőségeinken:
                            </p>

                            <div style="margin-top: 20px;">
                                <p><strong>Toronyszerver Kft.</strong></p>
                                <p>Tel: +36 1 234 5678</p>
                                <p>Email: info@toronyszerver.hu</p>
                                <p>Cím: 2471 Baracska Akácos utca 2430</p>
                            </div>

                            <a href="${API_URL}/quotes/${quote.id}/pdf" class="button">
                                Árajánlat letöltése PDF formátumban
                            </a>
                        </div>
                        <div class="footer">
                            <p>© ${new Date().getFullYear()} Toronyszerver Kft. Minden jog fenntartva.</p>
                            <p>Ez az email és annak mellékletei bizalmas információkat tartalmazhatnak.</p>
                        </div>
                    </body>
                    </html>
                `
            };

            await quoteService.sendQuote(quote.id, emailTemplate);
            setSuccess('Árajánlat sikeresen elküldve emailben!');
            fetchQuotes();
        } catch (err) {
            setError(err.message || 'Hiba történt az email küldése során.');
        } finally {
            setLoading(false);
        }
    };

    const fetchQuotes = async () => {
        try {
            setLoading(true);
            const response = await quoteService.getAllQuotes();
            setQuotes(response.quotes);
        } catch (err) {
            console.error('Fetch quotes error:', err);
            setError(err.message || 'Hiba történt az árajánlatok betöltése során.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 3) { // Árajánlatok tab
            fetchQuotes();
        }
    }, [activeTab]);

    const resetQuoteForm = () => {
        setQuoteForm({
            projectName: '',
            projectType: '',
            clientName: '',
            clientEmail: '',
            clientPhone: '',
            description: '',
            subtotal: 0,
            tax: 27,
            total: 0,
            validUntil: '',
            terms: '',
            notes: ''
        });
        setQuoteItems([]);
        setSelectedQuote(null);
    };

    const handleQuoteDialog = (quote = null) => {
        if (quote) {
            setSelectedQuote(quote);
            setQuoteForm({
                projectName: quote.project_name || '',
                projectType: quote.project_type || '',
                clientName: quote.client_name || '',
                clientEmail: quote.client_email || '',
                clientPhone: quote.client_phone || '',
                description: quote.description || '',
                subtotal: Number(quote.subtotal) || 0,
                tax: Number(quote.tax_rate) || 27,
                total: Number(quote.total) || 0,
                validUntil: quote.valid_until ? quote.valid_until.split('T')[0] : '',
                terms: quote.terms || '',
                notes: quote.notes || ''
            });
            
            // Tételek konvertálása a megfelelő formátumra
            const convertedItems = quote.items ? quote.items.map(item => ({
                id: item.id || Date.now(),
                category: item.category || '',
                description: item.description || '',
                quantity: Number(item.quantity) || 0,
                unit: item.unit || 'db',
                unitPrice: Number(item.unit_price) || 0,
                total: Number(item.total) || 0
            })) : [];
            
            setQuoteItems(convertedItems);
        } else {
            resetQuoteForm();
        }
        setQuoteDialog(true);
    };

    // Keresés és szűrés
    useEffect(() => {
        if (quotes.length > 0) {
            const filtered = quotes.filter(quote => {
                const quoteNumber = `AJ-${new Date().getFullYear()}${String(quote.id).padStart(4, '0')}`;
                return quoteNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    quote.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    quote.client_name.toLowerCase().includes(searchQuery.toLowerCase());
            });
            setFilteredQuotes(filtered);
        }
    }, [searchQuery, quotes]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 70px)">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="admin-page">
            <Box className="admin-header">
                <Box className="user-info">
                    <Avatar className="user-avatar">
                        {user?.name?.charAt(0) || 'A'}
                    </Avatar>
                    <Box className="user-details">
                        <Typography variant="h6">{user?.name || 'Admin'}</Typography>
                        <Typography>{user?.email}</Typography>
                    </Box>
                </Box>
                <Box className="action-buttons">
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setActiveTab(3)}
                    >
                        Beállítások
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                    >
                        Kijelentkezés
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert 
                    severity="error" 
                    onClose={() => setError(null)} 
                    sx={{ 
                        mb: 2,
                        borderRadius: '12px',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert 
                    severity="success" 
                    onClose={() => setSuccess(null)} 
                    sx={{ 
                        mb: 2,
                        borderRadius: '12px',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                >
                    {success}
                </Alert>
            )}

            <Container maxWidth="xl">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange} 
                        centered
                        variant="fullWidth"
                    >
                        <Tab 
                            icon={<DashboardIcon />} 
                            label="Áttekintés" 
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1
                            }}
                        />
                        <Tab 
                            icon={<AdminIcon />} 
                            label="Projektek" 
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1
                            }}
                        />
                        <Tab 
                            icon={<GroupIcon />} 
                            label="Felhasználók"
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1
                            }}
                        />
                        <Tab 
                            icon={<QuoteIcon />} 
                            label="Árajánlatok"
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1
                            }}
                        />
                        <Tab 
                            icon={<SettingsIcon />} 
                            label="Beállítások"
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1
                            }}
                        />
                    </Tabs>
                </Box>

                {/* Dashboard */}
                {activeTab === 0 && (
                    <Box sx={{ mt: 4 }}>
                        {/* Quick Actions */}
                        <Box className="quick-actions" sx={{ mb: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                Gyors műveletek
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        className="action-card"
                                        onClick={() => handleProjectDialog()}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <CardContent>
                                            <AssignmentIcon sx={{ fontSize: 40, color: '#2c5049', mb: 1 }} />
                                            <Typography variant="h6">Új projekt</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Projekt létrehozása
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        className="action-card"
                                        onClick={() => handleUserDialog()}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <CardContent>
                                            <PeopleIcon sx={{ fontSize: 40, color: '#2c5049', mb: 1 }} />
                                            <Typography variant="h6">Új felhasználó</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Felhasználó hozzáadása
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        className="action-card"
                                        onClick={() => setActiveTab(3)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <CardContent>
                                            <SettingsIcon sx={{ fontSize: 40, color: '#2c5049', mb: 1 }} />
                                            <Typography variant="h6">Beállítások</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Rendszer beállítások
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card 
                                        className="action-card"
                                        onClick={() => handleQuoteDialog()}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <CardContent>
                                            <QuoteIcon sx={{ fontSize: 40, color: '#2c5049', mb: 1 }} />
                                            <Typography variant="h6">Új árajánlat</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Árajánlat készítése
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Statistics */}
                        <Box className="statistics" sx={{ mb: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                Statisztikák
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <AssignmentIcon sx={{ color: '#2c5049', mr: 1 }} />
                                                <Typography variant="h6">Projektek</Typography>
                                            </Box>
                                            <Typography variant="h4" sx={{ mb: 1 }}>
                                                {dashboardStats.totalProjects}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {dashboardStats.activeProjects} aktív projekt
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <PeopleIcon sx={{ color: '#2c5049', mr: 1 }} />
                                                <Typography variant="h6">Felhasználók</Typography>
                                            </Box>
                                            <Typography variant="h4" sx={{ mb: 1 }}>
                                                {dashboardStats.totalUsers}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Regisztrált felhasználó
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <VisibilityIcon sx={{ color: '#2c5049', mr: 1 }} />
                                                <Typography variant="h6">Látogatások</Typography>
                                            </Box>
                                            <Typography variant="h4" sx={{ mb: 1 }}>
                                                {dashboardStats.totalViews.toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Összes oldal megtekintés
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <StarIcon sx={{ color: '#2c5049', mr: 1 }} />
                                                <Typography variant="h6">Teljesítmény</Typography>
                                            </Box>
                                            <Typography variant="h4" sx={{ mb: 1 }}>
                                                {Math.round((dashboardStats.projectStats.completed / dashboardStats.totalProjects) * 100)}%
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Befejezett projektek aránya
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Page Views */}
                        <Box className="page-views" sx={{ mb: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                Oldal látogatottság
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Látogatottsági trend
                                            </Typography>
                                            {/* Itt jöhet majd egy Chart komponens */}
                                            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography color="text.secondary">
                                                    Látogatottsági grafikon helye
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Legnépszerűbb oldalak
                                            </Typography>
                                            <List>
                                                {dashboardStats.recentViews.map((page, index) => (
                                                    <ListItem key={index} sx={{ px: 0 }}>
                                                        <ListItemText 
                                                            primary={page.page}
                                                            secondary={`${page.views.toLocaleString()} megtekintés`}
                                                        />
                                                        <Typography 
                                                            color="success.main"
                                                            sx={{ 
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <TrendingUpIcon sx={{ mr: 0.5 }} />
                                                            {page.trend}
                                                        </Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                )}

                {/* Projektek */}
                {activeTab === 1 && (
                    <Box sx={{ mt: 4 }}>
                        <Box className="section-header">
                            <Typography variant="h5">Projektek kezelése</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleProjectDialog()}
                            >
                                Új projekt
                            </Button>
                        </Box>

                        <Box className="projects-grid">
                            {loading ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '200px'
                                }}>
                                    <CircularProgress sx={{ color: '#2c5049' }} />
                                </Box>
                            ) : projects.length === 0 ? (
                                <Box sx={{ 
                                    textAlign: 'center',
                                    py: 4,
                                    color: '#666'
                                }}>
                                    <Typography variant="h6" gutterBottom>
                                        Nincs még projekt
                                    </Typography>
                                    <Typography>
                                        Kattints az "Új projekt" gombra a létrehozáshoz
                                    </Typography>
                                </Box>
                            ) : (
                                projects.map((project) => (
                                    <Card key={project.id} className="project-card">
                                        {project.images && project.images[0] && (
                                            <Box 
                                                component="img"
                                                src={`${API_URL}/uploads/projects/${project.images[0]}`}
                                                alt={project.title}
                                                sx={{
                                                    width: '100%',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    borderTopLeftRadius: '16px',
                                                    borderTopRightRadius: '16px'
                                                }}
                                                onClick={() => setSelectedImage(project.images[0])}
                                            />
                                        )}
                                        <CardContent>
                                            <Typography variant="h6" noWrap>
                                                {project.title}
                                            </Typography>
                                            <Box className="project-info">
                                                <Box className="info-item">
                                                    <BusinessIcon fontSize="small" />
                                                    <Typography>{project.location || 'Nincs megadva helyszín'}</Typography>
                                                </Box>
                                                <Box className="info-item">
                                                    {projectTypes.find(t => t.value === project.type)?.icon}
                                                    <Typography>
                                                        {projectTypes.find(t => t.value === project.type)?.label || 'Egyéb'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box className="project-status">
                                                <CircularProgress 
                                                    variant="determinate" 
                                                    value={project.status === 'completed' ? 100 : project.status === 'in-progress' ? 50 : 0}
                                                    size={20}
                                                    sx={{
                                                        color: project.status === 'completed' ? '#2c5049' : 
                                                               project.status === 'in-progress' ? '#1565c0' : '#e65100'
                                                    }}
                                                />
                                                <Typography>
                                                    {project.status === 'completed' ? 'Befejezett' :
                                                     project.status === 'in-progress' ? 'Folyamatban' : 'Függőben'}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                startIcon={<EditIcon />}
                                                onClick={() => handleProjectDialog(project)}
                                            >
                                                Szerkesztés
                                            </Button>
                                            <Button
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteProject(project.id)}
                                            >
                                                Törlés
                                            </Button>
                                        </CardActions>
                                    </Card>
                                ))
                            )}
                        </Box>
                    </Box>
                )}

                {/* Felhasználók */}
                {activeTab === 2 && (
                    <Box sx={{ mt: 4 }}>
                        <Box className="section-header">
                            <Typography variant="h5">Felhasználók kezelése</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleUserDialog()}
                            >
                                Új felhasználó
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            {loading ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    minHeight: '200px'
                                }}>
                                    <CircularProgress sx={{ color: '#2c5049' }} />
                                </Box>
                            ) : users.map((user) => (
                                <Grid item xs={12} md={6} lg={4} key={user.id}>
                                    <Card>
                                        <CardContent>
                                            <Box sx={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: 2
                                            }}>
                                                <Avatar sx={{ 
                                                    bgcolor: '#2c5049',
                                                    width: 56,
                                                    height: 56
                                                }}>
                                                    {user.name.charAt(0)}
                                                </Avatar>
                                                    <Box>
                                                    <Typography variant="h6">
                                                        {user.name}
                                                    </Typography>
                                                        <Typography color="textSecondary">
                                                            {user.username}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Chip 
                                                    label={user.role === 'admin' ? 'Adminisztrátor' : 'Tulajdonos'}
                                                    color={user.role === 'admin' ? 'primary' : 'secondary'}
                                                    sx={{ 
                                                        borderRadius: '8px',
                                                        bgcolor: user.role === 'admin' ? 'rgba(44, 80, 73, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                                                        color: user.role === 'admin' ? '#2c5049' : '#9c27b0'
                                                    }}
                                                />
                                            </Box>
                                            <Box className="project-info">
                                                <Box className="info-item">
                                                    <EmailIcon fontSize="small" />
                                                    <Typography>{user.email}</Typography>
                                                </Box>
                                                {user.phone && (
                                                    <Box className="info-item">
                                                        <PhoneIcon fontSize="small" />
                                                        <Typography>{user.phone}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                startIcon={<EditIcon />}
                                                        onClick={() => handleUserDialog(user)}
                                                    >
                                                Szerkesztés
                                            </Button>
                                            <Button
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                            onClick={() => handleDeleteUser(user.id)}
                                                disabled={user.role === 'admin'}
                                            >
                                                Törlés
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Árajánlatok */}
                {activeTab === 3 && (
                    <Box sx={{ mt: 4 }}>
                        <Box className="section-header" sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3
                        }}>
                            <Typography variant="h5">Árajánlatok kezelése</Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    placeholder="Keresés..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: 250 }}
                                />
                                <Box sx={{ 
                                    display: 'flex', 
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}>
                                    <IconButton 
                                        size="small"
                                        onClick={() => setQuoteView('grid')}
                                        sx={{ 
                                            bgcolor: quoteView === 'grid' ? 'action.selected' : 'transparent',
                                            borderRadius: 0
                                        }}
                                    >
                                        <GridViewIcon />
                                    </IconButton>
                                    <IconButton 
                                        size="small"
                                        onClick={() => setQuoteView('list')}
                                        sx={{ 
                                            bgcolor: quoteView === 'list' ? 'action.selected' : 'transparent',
                                            borderRadius: 0
                                        }}
                                    >
                                        <ViewListIcon />
                                    </IconButton>
                                    <IconButton 
                                        size="small"
                                        onClick={() => setQuoteView('details')}
                                        sx={{ 
                                            bgcolor: quoteView === 'details' ? 'action.selected' : 'transparent',
                                            borderRadius: 0
                                        }}
                                    >
                                        <ViewHeadlineIcon />
                                    </IconButton>
                                </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setSelectedQuote(null);
                                    handleQuoteDialog();
                                }}
                                    sx={{ 
                                        bgcolor: '#2c5049',
                                        '&:hover': { bgcolor: '#1e3830' }
                                }}
                            >
                                Új árajánlat
                            </Button>
                            </Box>
                                                </Box>

                        {/* Mozaik nézet */}
                        {quoteView === 'grid' && (
                        <Grid container spacing={3}>
                                {filteredQuotes.map((quote) => (
                                <Grid item xs={12} md={6} lg={4} key={quote.id}>
                                        <Card sx={{ 
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            borderRadius: '12px',
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }
                                        }}>
                                        <CardContent>
                                            <Box sx={{ 
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                mb: 2
                                            }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                        {quote.project_name}
                                                </Typography>
                                                    <Chip
                                                        label={getStatusLabel(quote.status)}
                                                    color={getStatusColor(quote.status)}
                                                        size="small"
                                                        sx={{ borderRadius: '8px' }}
                                                />
                                            </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <BusinessIcon fontSize="small" sx={{ color: '#2c5049' }} />
                                                        <Typography>{quote.client_name}</Typography>
                                                </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CalendarIcon fontSize="small" sx={{ color: '#2c5049' }} />
                                                    <Typography>
                                                            Érvényes: {new Date(quote.valid_until).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <MoneyIcon fontSize="small" sx={{ color: '#2c5049' }} />
                                                        <Typography sx={{ fontWeight: 500 }}>
                                                            {Number(quote.total).toLocaleString()} Ft
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                            <CardActions sx={{ 
                                                p: 2,
                                                borderTop: '1px solid rgba(0,0,0,0.1)',
                                                display: 'flex',
                                                justifyContent: 'space-between'
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleQuoteDialog(quote)}
                                                        sx={{ color: '#2c5049' }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExportPDF(quote)}
                                                        sx={{ color: '#2c5049' }}
                                                    >
                                                        <PrintIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExportExcel(quote)}
                                                        sx={{ color: '#2c5049' }}
                                                    >
                                                        <TableViewIcon />
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleSendEmail(quote)}
                                                        disabled={!quote.client_email}
                                                        sx={{ 
                                                            color: quote.client_email ? '#2c5049' : 'action.disabled'
                                                        }}
                                                    >
                                                        <SendIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteQuote(quote.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Lista nézet */}
                        {quoteView === 'list' && (
                            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Azonosító</TableCell>
                                            <TableCell>Projekt neve</TableCell>
                                            <TableCell>Ügyfél</TableCell>
                                            <TableCell>Érvényesség</TableCell>
                                            <TableCell>Összeg</TableCell>
                                            <TableCell>Státusz</TableCell>
                                            <TableCell align="right">Műveletek</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredQuotes.map((quote) => (
                                            <TableRow key={quote.id} hover>
                                                <TableCell>{quote.id}</TableCell>
                                                <TableCell>{quote.project_name}</TableCell>
                                                <TableCell>{quote.client_name}</TableCell>
                                                <TableCell>
                                                    {new Date(quote.valid_until).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    {Number(quote.total).toLocaleString()} Ft
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusLabel(quote.status)}
                                                        color={getStatusColor(quote.status)}
                                                        size="small"
                                                        sx={{ borderRadius: '8px' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleQuoteDialog(quote)}
                                                            sx={{ color: '#2c5049' }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleExportPDF(quote)}
                                                            sx={{ color: '#2c5049' }}
                                                        >
                                                            <PrintIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSendEmail(quote)}
                                                            disabled={!quote.client_email}
                                                            sx={{ 
                                                                color: quote.client_email ? '#2c5049' : 'action.disabled'
                                                            }}
                                                        >
                                                            <SendIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteQuote(quote.id)}
                                                            sx={{ color: 'error.main' }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Részletes nézet */}
                        {quoteView === 'details' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {filteredQuotes.map((quote) => (
                                    <Card key={quote.id} sx={{ 
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}>
                                        <CardContent>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Box>
                                                            <Typography variant="overline" color="textSecondary">
                                                                Projekt adatok
                                                            </Typography>
                                                            <Typography variant="h6">
                                                                {quote.project_name}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Azonosító: {quote.id}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="overline" color="textSecondary">
                                                                Ügyfél adatok
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {quote.client_name}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {quote.client_email} • {quote.client_phone}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column',
                                                        gap: 2,
                                                        alignItems: 'flex-end'
                                                    }}>
                                                        <Chip
                                                            label={getStatusLabel(quote.status)}
                                                            color={getStatusColor(quote.status)}
                                                            sx={{ borderRadius: '8px' }}
                                                        />
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="overline" color="textSecondary">
                                                                Összeg
                                                            </Typography>
                                                            <Typography variant="h5" sx={{ color: '#2c5049' }}>
                                                                {Number(quote.total).toLocaleString()} Ft
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                Érvényes: {new Date(quote.valid_until).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                        <CardActions sx={{ 
                                            p: 2,
                                            borderTop: '1px solid rgba(0,0,0,0.1)',
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: 1
                                        }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleQuoteDialog(quote)}
                                                size="small"
                                            >
                                                Szerkesztés
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PrintIcon />}
                                                onClick={() => handleExportPDF(quote)}
                                                size="small"
                                            >
                                                PDF
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<TableViewIcon />}
                                                onClick={() => handleExportExcel(quote)}
                                                size="small"
                                            >
                                                Excel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={<SendIcon />}
                                                onClick={() => handleSendEmail(quote)}
                                                disabled={!quote.client_email}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: '#2c5049',
                                                    '&:hover': { bgcolor: '#1e3830' }
                                                }}
                                            >
                                                Küldés
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteQuote(quote.id)}
                                                size="small"
                                            >
                                                Törlés
                                            </Button>
                                        </CardActions>
                                    </Card>
                                ))}
                            </Box>
                        )}

                        {filteredQuotes.length === 0 && (
                                    <Box sx={{ 
                                        textAlign: 'center',
                                        py: 4,
                                        color: '#666'
                                    }}>
                                        <Typography variant="h6" gutterBottom>
                                    {searchQuery ? 'Nincs találat' : 'Nincs még árajánlat'}
                                        </Typography>
                                        <Typography>
                                    {searchQuery ? 'Próbálja meg más keresési feltételekkel' : 'Kattints az "Új árajánlat" gombra a létrehozáshoz'}
                                        </Typography>
                    </Box>
                            )}
                    </Box>
                )}

                {/* Beállítások */}
                {activeTab === 4 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h5" gutterBottom>
                            Beállítások
                        </Typography>
                        <UnderDevelopment />
                    </Box>
                )}
            </Container>

            {/* Nagyított kép dialog */}
            <Dialog
                open={!!selectedImage}
                onClose={handleCloseImageViewer}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ position: 'relative', p: 0, overflow: 'hidden', height: '90vh' }}>
                    <IconButton
                        onClick={handleCloseImageViewer}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.4)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.6)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={`${API_URL}/uploads/projects/${selectedImage}`}
                        alt="Nagyított kép"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                    />
                </DialogContent>
            </Dialog>

            {/* Projekt Dialog */}
            <Dialog
                open={projectDialog}
                onClose={() => setProjectDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedProject ? 'Projekt szerkesztése' : 'Új projekt létrehozása'}
                    <IconButton
                        onClick={() => setProjectDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleProjectSubmit}>
                    <DialogContent dividers>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Projekt címe"
                                    name="title"
                                    value={projectForm.title}
                                    onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Projekt típusa</InputLabel>
                                    <Select
                                        value={projectForm.type}
                                        onChange={(e) => setProjectForm({ ...projectForm, type: e.target.value })}
                                        label="Projekt típusa"
                                    >
                                        {projectTypes.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {type.icon}
                                                    {type.label}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>Válassza ki a projekt típusát</FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Helyszín"
                                    name="location"
                                    value={projectForm.location}
                                    onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Leírás"
                                    name="description"
                                    value={projectForm.description}
                                    onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Kezdés dátuma"
                                    name="startDate"
                                    value={projectForm.startDate}
                                    onChange={(e) => setProjectForm({ ...projectForm, startDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Befejezés dátuma"
                                    name="endDate"
                                    value={projectForm.endDate}
                                    onChange={(e) => setProjectForm({ ...projectForm, endDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Státusz</InputLabel>
                                    <Select
                                        value={projectForm.status}
                                        onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                                        label="Státusz"
                                    >
                                        <MenuItem value="pending">Függőben</MenuItem>
                                        <MenuItem value="in_progress">Folyamatban</MenuItem>
                                        <MenuItem value="completed">Befejezett</MenuItem>
                                        <MenuItem value="cancelled">Törölt</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={projectForm.featured}
                                            onChange={(e) => setProjectForm({ ...projectForm, featured: e.target.checked })}
                                            disabled={projectForm.status !== 'completed'}
                                        />
                                    }
                                    label="Kiemelt referencia"
                                />
                                {projectForm.status !== 'completed' && (
                                    <FormHelperText>
                                        Csak befejezett projektet lehet kiemelt referenciaként megjelölni
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Képek
                                    </Typography>
                                    {projectForm.images && projectForm.images.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                            {projectForm.images.map((image, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        position: 'relative',
                                                        width: 100,
                                                        height: 100,
                                                    }}
                                                >
                                                    <img
                                                        src={`${API_URL}/uploads/projects/${image}`}
                                                        alt={`Projekt kép ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px'
                                                        }}
                                                        onClick={() => handleImageClick(image)}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteImage(selectedProject?.id, image)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            right: 4,
                                                            bgcolor: 'rgba(0,0,0,0.4)',
                                                            color: 'white',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(0,0,0,0.6)'
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        fullWidth
                                    >
                                        Képek feltöltése
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*"
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files);
                                                setProjectForm(prev => ({
                                                    ...prev,
                                                    newImages: [...(prev.newImages || []), ...files]
                                                }));
                                            }}
                                        />
                                    </Button>
                                    {projectForm.newImages && projectForm.newImages.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Feltöltésre váró képek:
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {projectForm.newImages.map((file, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            position: 'relative',
                                                            width: 100,
                                                            height: 100
                                                        }}
                                                    >
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt={`Új kép ${index + 1}`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '4px'
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setProjectForm(prev => ({
                                                                    ...prev,
                                                                    newImages: prev.newImages.filter((_, i) => i !== index)
                                                                }));
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: 'rgba(0,0,0,0.4)',
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(0,0,0,0.6)'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setProjectDialog(false)}>
                            Mégse
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {selectedProject ? 'Mentés' : 'Létrehozás'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Felhasználó Dialog */}
            <Dialog
                open={userDialog}
                onClose={() => setUserDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {selectedUser ? 'Felhasználó szerkesztése' : 'Új felhasználó létrehozása'}
                    <IconButton
                        onClick={() => setUserDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleUserSubmit}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Felhasználónév"
                                    value={userForm.username}
                                    onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                                    required
                                />
                            </Grid>
                            {!selectedUser && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Jelszó"
                                        type="password"
                                        value={userForm.password}
                                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                        required
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Név"
                                    value={userForm.name}
                                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={userForm.email}
                                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Telefonszám"
                                    value={userForm.phone}
                                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Szerepkör</InputLabel>
                                    <Select
                                    value={userForm.role}
                                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                        label="Szerepkör"
                                    >
                                        <MenuItem value="admin">Adminisztrátor</MenuItem>
                                        <MenuItem value="owner">Tulajdonos</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUserDialog(false)}>
                            Mégse
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {selectedUser ? 'Mentés' : 'Létrehozás'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Árajánlat Dialog */}
            <Dialog
                open={quoteDialog}
                onClose={() => setQuoteDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Új árajánlat készítése
                    <IconButton
                        onClick={() => setQuoteDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleQuoteSubmit}>
                    <DialogContent dividers>
                        <Grid container spacing={3}>
                            {/* Projekt és ügyfél adatok */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Projekt adatok
                                    </Typography>
                                    <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                                label="Projekt neve"
                                                value={quoteForm.projectName}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, projectName: e.target.value })}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth required>
                                                <InputLabel>Projekt típusa</InputLabel>
                                                <Select
                                                    value={quoteForm.projectType}
                                                    onChange={(e) => setQuoteForm({ ...quoteForm, projectType: e.target.value })}
                                                    label="Projekt típusa"
                                                >
                                                    {projectTypes.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {type.icon}
                                                                {type.label}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2, height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Ügyfél adatok
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Ügyfél neve"
                                                value={quoteForm.clientName}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, clientName: e.target.value })}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                type="email"
                                                value={quoteForm.clientEmail}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, clientEmail: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Telefonszám"
                                                value={quoteForm.clientPhone}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, clientPhone: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>

                            {/* Költségtételek */}
                            <Grid item xs={12}>
                                <Card sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Költségtételek
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddQuoteItem}
                                        >
                                            Új tétel
                                        </Button>
                                    </Box>
                                    {quoteItems.map((item, index) => (
                                        <Box
                                            key={item.id}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                                borderRadius: 1,
                                                position: 'relative'
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const newItems = quoteItems.filter(i => i.id !== item.id);
                                                    setQuoteItems(newItems);
                                                    calculateTotals(newItems);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: 'error.main'
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Kategória</InputLabel>
                                                        <Select
                                                            value={item.category}
                                                            onChange={(e) => handleQuoteItemChange(item.id, 'category', e.target.value)}
                                                            label="Kategória"
                                                        >
                                                            {itemCategories.map((cat) => (
                                                                <MenuItem key={cat.id} value={cat.id}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {cat.icon}
                                                                        {cat.label}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <TextField
                                                        fullWidth
                                                        label="Megnevezés"
                                                        value={item.description}
                                                        onChange={(e) => handleQuoteItemChange(item.id, 'description', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <TextField
                                                            sx={{ flex: 1 }}
                                                            label="Mennyiség"
                                    type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleQuoteItemChange(item.id, 'quantity', parseFloat(e.target.value))}
                                                        />
                                                        <FormControl sx={{ minWidth: 80 }}>
                                                            <InputLabel>Egység</InputLabel>
                                                            <Select
                                                                value={item.unit}
                                                                onChange={(e) => handleQuoteItemChange(item.id, 'unit', e.target.value)}
                                                                label="Egység"
                                                            >
                                                                <MenuItem value="db">db</MenuItem>
                                                                <MenuItem value="m²">m²</MenuItem>
                                                                <MenuItem value="m³">m³</MenuItem>
                                                                <MenuItem value="fm">fm</MenuItem>
                                                                <MenuItem value="óra">óra</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Egységár"
                                                        type="number"
                                                        value={item.unitPrice}
                                                        onChange={(e) => handleQuoteItemChange(item.id, 'unitPrice', parseFloat(e.target.value))}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">Ft</InputAdornment>
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Összesen"
                                                        value={item.total.toLocaleString()}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">Ft</InputAdornment>,
                                                            readOnly: true
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                </Card>
                            </Grid>

                            {/* Összesítés */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Összesítés
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography>Nettó összesen:</Typography>
                                                <Typography>{quoteForm.subtotal.toLocaleString()} Ft</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography>ÁFA ({quoteForm.tax}%):</Typography>
                                                <Typography>{(quoteForm.subtotal * quoteForm.tax / 100).toLocaleString()} Ft</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                                <Typography variant="h6">Bruttó összesen:</Typography>
                                                <Typography variant="h6" color="primary">
                                                    {quoteForm.total.toLocaleString()} Ft
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>

                            {/* További adatok */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        További adatok
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Érvényesség"
                                                type="date"
                                                value={quoteForm.validUntil}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, validUntil: e.target.value })}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Megjegyzések"
                                                multiline
                                                rows={4}
                                                value={quoteForm.notes}
                                                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, gap: 1 }}>
                        <Button onClick={() => setQuoteDialog(false)}>
                            Mégse
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={() => {/* PDF generálás */}}
                        >
                            PDF mentése
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            Mentés
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
};

export default Admin; 